import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  addDoc, 
  updateDoc,
  doc,
  getDoc,
  orderBy, 
  serverTimestamp,
  arrayUnion,
  arrayRemove,
  limit
} from 'firebase/firestore';
import { db } from '../config/firebase';
import './EnhancedBookClubs.css';

function EnhancedBookClubs() {
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('my-clubs');
  const [bookClubs, setBookClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateClub, setShowCreateClub] = useState(false);
  const [selectedClub, setSelectedClub] = useState(null);
  const [showDiscussions, setShowDiscussions] = useState(false);
  const [discussions, setDiscussions] = useState([]);
  const [meetings, setMeetings] = useState([]);
  
  // Form states
  const [clubForm, setClubForm] = useState({
    name: '',
    description: '',
    currentBook: '',
    isPrivate: false
  });
  
  const [discussionForm, setDiscussionForm] = useState({
    title: '',
    content: '',
    type: 'general'
  });

  const [meetingForm, setMeetingForm] = useState({
    title: '',
    description: '',
    date: '',
    time: '',
    location: '',
    type: 'in-person'
  });

  useEffect(() => {
    fetchBookClubs();
  }, [currentUser, activeTab]);

  const fetchBookClubs = async () => {
    if (!currentUser) return;

    try {
      setLoading(true);
      let clubsQuery;
      
      if (activeTab === 'my-clubs') {
        // Get clubs where user is creator or member
        clubsQuery = query(
          collection(db, 'bookClubs'),
          orderBy('updatedAt', 'desc')
        );
      } else {
        // Get all public clubs
        clubsQuery = query(
          collection(db, 'bookClubs'),
          where('isPrivate', '==', false),
          orderBy('updatedAt', 'desc'),
          limit(20)
        );
      }
      
      const querySnapshot = await getDocs(clubsQuery);
      const clubsData = [];
      
      for (const docSnap of querySnapshot.docs) {
        const club = { id: docSnap.id, ...docSnap.data() };
        
        // Get creator profile
        const creatorDoc = await getDoc(doc(db, 'userProfiles', club.creatorId));
        if (creatorDoc.exists()) {
          club.creatorProfile = creatorDoc.data();
        }
        
        // Filter for my-clubs tab
        if (activeTab === 'my-clubs') {
          if (club.creatorId === currentUser.uid || 
              (club.memberIds && club.memberIds.includes(currentUser.uid))) {
            clubsData.push(club);
          }
        } else {
          clubsData.push(club);
        }
      }
      
      setBookClubs(clubsData);
    } catch (error) {
      console.error('Error fetching book clubs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchClubDiscussions = async (clubId) => {
    try {
      const discussionsQuery = query(
        collection(db, 'clubDiscussions'),
        where('clubId', '==', clubId),
        orderBy('createdAt', 'desc'),
        limit(20)
      );

      const querySnapshot = await getDocs(discussionsQuery);
      const discussionsData = [];
      
      for (const docSnap of querySnapshot.docs) {
        const discussion = { id: docSnap.id, ...docSnap.data() };
        
        // Get author profile
        const authorDoc = await getDoc(doc(db, 'userProfiles', discussion.authorId));
        if (authorDoc.exists()) {
          discussion.authorProfile = authorDoc.data();
        }
        
        discussionsData.push(discussion);
      }
      
      setDiscussions(discussionsData);
    } catch (error) {
      console.error('Error fetching discussions:', error);
    }
  };

  const fetchClubMeetings = async (clubId) => {
    try {
      const meetingsQuery = query(
        collection(db, 'clubMeetings'),
        where('clubId', '==', clubId),
        orderBy('scheduledDate', 'desc'),
        limit(10)
      );

      const querySnapshot = await getDocs(meetingsQuery);
      const meetingsData = [];
      
      for (const docSnap of querySnapshot.docs) {
        const meeting = { id: docSnap.id, ...docSnap.data() };
        meetingsData.push(meeting);
      }
      
      setMeetings(meetingsData);
    } catch (error) {
      console.error('Error fetching meetings:', error);
    }
  };

  const createClub = async (e) => {
    e.preventDefault();
    
    try {
      const clubData = {
        name: clubForm.name.trim(),
        description: clubForm.description.trim(),
        currentBook: clubForm.currentBook.trim(),
        creatorId: currentUser.uid,
        memberIds: [currentUser.uid],
        isPrivate: clubForm.isPrivate,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        discussionCount: 0,
        meetingCount: 0
      };

      await addDoc(collection(db, 'bookClubs'), clubData);
      
      setClubForm({ name: '', description: '', currentBook: '', isPrivate: false });
      setShowCreateClub(false);
      fetchBookClubs();
      
      alert('Book club created successfully!');
    } catch (error) {
      console.error('Error creating club:', error);
      alert('Failed to create club. Please try again.');
    }
  };

  const joinClub = async (clubId) => {
    try {
      await updateDoc(doc(db, 'bookClubs', clubId), {
        memberIds: arrayUnion(currentUser.uid),
        updatedAt: serverTimestamp()
      });
      
      fetchBookClubs();
      alert('Successfully joined the book club!');
    } catch (error) {
      console.error('Error joining club:', error);
      alert('Failed to join club. Please try again.');
    }
  };

  const leaveClub = async (clubId) => {
    try {
      await updateDoc(doc(db, 'bookClubs', clubId), {
        memberIds: arrayRemove(currentUser.uid),
        updatedAt: serverTimestamp()
      });
      
      fetchBookClubs();
      alert('Left the book club.');
    } catch (error) {
      console.error('Error leaving club:', error);
      alert('Failed to leave club. Please try again.');
    }
  };

  const createDiscussion = async (e) => {
    e.preventDefault();
    
    if (!selectedClub) return;
    
    try {
      const discussionData = {
        clubId: selectedClub.id,
        authorId: currentUser.uid,
        title: discussionForm.title.trim(),
        content: discussionForm.content.trim(),
        type: discussionForm.type,
        replies: [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      await addDoc(collection(db, 'clubDiscussions'), discussionData);
      
      // Update club discussion count
      await updateDoc(doc(db, 'bookClubs', selectedClub.id), {
        discussionCount: (selectedClub.discussionCount || 0) + 1,
        updatedAt: serverTimestamp()
      });
      
      setDiscussionForm({ title: '', content: '', type: 'general' });
      fetchClubDiscussions(selectedClub.id);
      
      alert('Discussion created successfully!');
    } catch (error) {
      console.error('Error creating discussion:', error);
      alert('Failed to create discussion. Please try again.');
    }
  };

  const scheduleMeeting = async (e) => {
    e.preventDefault();
    
    if (!selectedClub) return;
    
    try {
      const meetingDateTime = new Date(`${meetingForm.date}T${meetingForm.time}`);
      
      const meetingData = {
        clubId: selectedClub.id,
        organizerId: currentUser.uid,
        title: meetingForm.title.trim(),
        description: meetingForm.description.trim(),
        scheduledDate: meetingDateTime,
        location: meetingForm.location.trim(),
        type: meetingForm.type,
        attendees: [currentUser.uid],
        status: 'scheduled',
        createdAt: serverTimestamp()
      };

      await addDoc(collection(db, 'clubMeetings'), meetingData);
      
      // Update club meeting count
      await updateDoc(doc(db, 'bookClubs', selectedClub.id), {
        meetingCount: (selectedClub.meetingCount || 0) + 1,
        updatedAt: serverTimestamp()
      });
      
      setMeetingForm({ title: '', description: '', date: '', time: '', location: '', type: 'in-person' });
      fetchClubMeetings(selectedClub.id);
      
      alert('Meeting scheduled successfully!');
    } catch (error) {
      console.error('Error scheduling meeting:', error);
      alert('Failed to schedule meeting. Please try again.');
    }
  };

  const viewClubDetails = (club) => {
    setSelectedClub(club);
    setShowDiscussions(true);
    fetchClubDiscussions(club.id);
    fetchClubMeetings(club.id);
  };

  const ClubCard = ({ club }) => {
    const isMember = club.creatorId === currentUser.uid || 
                     (club.memberIds && club.memberIds.includes(currentUser.uid));
    const memberCount = club.memberIds ? club.memberIds.length : 1;

    return (
      <div className="club-card">
        <div className="club-header">
          <div className="club-info">
            <h3>{club.name}</h3>
            <p className="club-description">{club.description}</p>
            {club.currentBook && (
              <p className="current-book">📚 Currently reading: {club.currentBook}</p>
            )}
          </div>
          <div className="club-stats">
            <div className="stat-item">
              <span className="stat-number">{memberCount}</span>
              <span className="stat-label">Members</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">{club.discussionCount || 0}</span>
              <span className="stat-label">Discussions</span>
            </div>
          </div>
        </div>

        <div className="club-meta">
          <div className="club-creator">
            Created by {club.creatorProfile?.displayName || 'Unknown'}
          </div>
          <div className="club-privacy">
            {club.isPrivate ? '🔒 Private' : '🌍 Public'}
          </div>
        </div>

        <div className="club-actions">
          <button 
            className="btn btn-primary"
            onClick={() => viewClubDetails(club)}
          >
            📖 View Details
          </button>
          {!isMember ? (
            <button 
              className="btn btn-secondary"
              onClick={() => joinClub(club.id)}
            >
              🤝 Join Club
            </button>
          ) : club.creatorId !== currentUser.uid && (
            <button 
              className="btn btn-outline"
              onClick={() => leaveClub(club.id)}
            >
              🚪 Leave Club
            </button>
          )}
        </div>
      </div>
    );
  };

  const ClubDetailsModal = () => (
    <div className="modal-overlay">
      <div className="club-details-modal">
        <div className="modal-header">
          <h2>🏛️ {selectedClub?.name}</h2>
          <button className="close-button" onClick={() => setShowDiscussions(false)}>×</button>
        </div>

        <div className="club-details-content">
          <div className="club-details-tabs">
            <button className="tab-btn active">💬 Discussions ({discussions.length})</button>
            <button className="tab-btn">📅 Meetings ({meetings.length})</button>
          </div>

          <div className="discussions-section">
            <div className="section-header">
              <h3>Recent Discussions</h3>
              <button className="btn btn-primary btn-sm">
                ➕ New Discussion
              </button>
            </div>

            <form onSubmit={createDiscussion} className="discussion-form">
              <div className="form-row">
                <input
                  type="text"
                  placeholder="Discussion title..."
                  value={discussionForm.title}
                  onChange={(e) => setDiscussionForm({...discussionForm, title: e.target.value})}
                  className="form-input"
                  required
                />
                <select
                  value={discussionForm.type}
                  onChange={(e) => setDiscussionForm({...discussionForm, type: e.target.value})}
                  className="form-select"
                >
                  <option value="general">💬 General</option>
                  <option value="book-discussion">📚 Book Discussion</option>
                  <option value="question">❓ Question</option>
                  <option value="announcement">📢 Announcement</option>
                </select>
              </div>
              <textarea
                placeholder="What would you like to discuss?"
                value={discussionForm.content}
                onChange={(e) => setDiscussionForm({...discussionForm, content: e.target.value})}
                className="form-textarea"
                rows={3}
                required
              />
              <button type="submit" className="btn btn-primary">
                💬 Start Discussion
              </button>
            </form>

            <div className="discussions-list">
              {discussions.map(discussion => (
                <div key={discussion.id} className="discussion-item">
                  <div className="discussion-header">
                    <div className="discussion-type">
                      {discussion.type === 'book-discussion' && '📚'}
                      {discussion.type === 'question' && '❓'}
                      {discussion.type === 'announcement' && '📢'}
                      {discussion.type === 'general' && '💬'}
                    </div>
                    <h4>{discussion.title}</h4>
                    <div className="discussion-meta">
                      By {discussion.authorProfile?.displayName || 'Unknown'} • 
                      {discussion.createdAt?.toDate?.()?.toLocaleDateString() || 'Unknown date'}
                    </div>
                  </div>
                  <p className="discussion-content">{discussion.content}</p>
                  <div className="discussion-actions">
                    <button className="btn btn-outline btn-sm">
                      💬 Reply ({discussion.replies?.length || 0})
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="meetings-section">
            <div className="section-header">
              <h3>Upcoming Meetings</h3>
              <button className="btn btn-secondary btn-sm">
                📅 Schedule Meeting
              </button>
            </div>

            <form onSubmit={scheduleMeeting} className="meeting-form">
              <div className="form-row">
                <input
                  type="text"
                  placeholder="Meeting title..."
                  value={meetingForm.title}
                  onChange={(e) => setMeetingForm({...meetingForm, title: e.target.value})}
                  className="form-input"
                  required
                />
                <select
                  value={meetingForm.type}
                  onChange={(e) => setMeetingForm({...meetingForm, type: e.target.value})}
                  className="form-select"
                >
                  <option value="in-person">🏢 In Person</option>
                  <option value="virtual">💻 Virtual</option>
                  <option value="hybrid">🔗 Hybrid</option>
                </select>
              </div>
              <div className="form-row">
                <input
                  type="date"
                  value={meetingForm.date}
                  onChange={(e) => setMeetingForm({...meetingForm, date: e.target.value})}
                  className="form-input"
                  required
                />
                <input
                  type="time"
                  value={meetingForm.time}
                  onChange={(e) => setMeetingForm({...meetingForm, time: e.target.value})}
                  className="form-input"
                  required
                />
              </div>
              <input
                type="text"
                placeholder="Location or meeting link..."
                value={meetingForm.location}
                onChange={(e) => setMeetingForm({...meetingForm, location: e.target.value})}
                className="form-input"
              />
              <textarea
                placeholder="Meeting description..."
                value={meetingForm.description}
                onChange={(e) => setMeetingForm({...meetingForm, description: e.target.value})}
                className="form-textarea"
                rows={2}
              />
              <button type="submit" className="btn btn-secondary">
                📅 Schedule Meeting
              </button>
            </form>

            <div className="meetings-list">
              {meetings.map(meeting => (
                <div key={meeting.id} className="meeting-item">
                  <div className="meeting-header">
                    <div className="meeting-type">
                      {meeting.type === 'virtual' && '💻'}
                      {meeting.type === 'in-person' && '🏢'}
                      {meeting.type === 'hybrid' && '🔗'}
                    </div>
                    <h4>{meeting.title}</h4>
                    <div className="meeting-datetime">
                      📅 {meeting.scheduledDate?.toDate?.()?.toLocaleDateString()} at {meeting.scheduledDate?.toDate?.()?.toLocaleTimeString()}
                    </div>
                  </div>
                  {meeting.description && (
                    <p className="meeting-description">{meeting.description}</p>
                  )}
                  <div className="meeting-location">
                    📍 {meeting.location}
                  </div>
                  <div className="meeting-actions">
                    <button className="btn btn-primary btn-sm">
                      ✋ Attend ({meeting.attendees?.length || 0})
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="clubs-loading">
        <div className="loading-spinner"></div>
        <p>Loading book clubs...</p>
      </div>
    );
  }

  return (
    <div className="enhanced-book-clubs">
      <div className="clubs-header">
        <h2>🏛️ Book Clubs</h2>
        <p>Join discussions and connect with fellow readers</p>
        <button 
          className="btn btn-primary"
          onClick={() => setShowCreateClub(true)}
        >
          ➕ Create Club
        </button>
      </div>

      <div className="clubs-tabs">
        <button 
          className={`tab-btn ${activeTab === 'my-clubs' ? 'active' : ''}`}
          onClick={() => setActiveTab('my-clubs')}
        >
          📚 My Clubs
        </button>
        <button 
          className={`tab-btn ${activeTab === 'discover' ? 'active' : ''}`}
          onClick={() => setActiveTab('discover')}
        >
          🔍 Discover
        </button>
      </div>

      <div className="clubs-grid">
        {bookClubs.map(club => (
          <ClubCard key={club.id} club={club} />
        ))}
      </div>

      {bookClubs.length === 0 && (
        <div className="empty-state">
          <div className="empty-icon">🏛️</div>
          <h3>
            {activeTab === 'my-clubs' 
              ? 'No book clubs yet' 
              : 'No public clubs found'
            }
          </h3>
          <p>
            {activeTab === 'my-clubs'
              ? 'Create your first book club or discover existing ones!'
              : 'Be the first to create a public book club!'
            }
          </p>
        </div>
      )}

      {showCreateClub && (
        <div className="modal-overlay">
          <div className="create-club-modal">
            <div className="modal-header">
              <h2>✨ Create New Book Club</h2>
              <button className="close-button" onClick={() => setShowCreateClub(false)}>×</button>
            </div>
            
            <form onSubmit={createClub} className="create-club-form">
              <div className="form-group">
                <label>Club Name</label>
                <input
                  type="text"
                  value={clubForm.name}
                  onChange={(e) => setClubForm({...clubForm, name: e.target.value})}
                  placeholder="Enter club name..."
                  className="form-input"
                  required
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={clubForm.description}
                  onChange={(e) => setClubForm({...clubForm, description: e.target.value})}
                  placeholder="Describe your book club..."
                  className="form-textarea"
                  rows={3}
                  required
                />
              </div>

              <div className="form-group">
                <label>Current Book (Optional)</label>
                <input
                  type="text"
                  value={clubForm.currentBook}
                  onChange={(e) => setClubForm({...clubForm, currentBook: e.target.value})}
                  placeholder="What book are you currently reading?"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={clubForm.isPrivate}
                    onChange={(e) => setClubForm({...clubForm, isPrivate: e.target.checked})}
                  />
                  🔒 Make this club private
                </label>
              </div>

              <div className="modal-actions">
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={() => setShowCreateClub(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  🏛️ Create Club
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDiscussions && selectedClub && <ClubDetailsModal />}
    </div>
  );
}

export default EnhancedBookClubs;